import {
  Circle, CircleX, ExternalLink, Phone, PhoneOutgoing, RotateCcw, SkipForward, Square,
  Voicemail, Zap,
} from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Callout, Spinner } from "@/components/ui/feedback";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { leadName } from "@/lib/campaign";
import {
  answerState, attemptFromText, attemptTiles, attemptVoicemailText, attemptsText, canStopRedials,
  dialStageText, dialStrip, dialWaitText, leadDetails, leadPlace, recentDialedLead, redialTitle,
  secondsLeft, stripVoicemailNote, voicemailNote, type AttemptTile,
} from "@/lib/dialer";
import { secondsSince } from "@/lib/manual-call";
import {
  useCallDetection, useCallDispositions, useDialedLead, useDialerCall, useDialerOffer,
  useHeardVoicemail, useOfferAction, useStopRedials, useWatchedDial,
} from "@/lib/queries";
import type { DialDetection, DialerCall, DialerOffer } from "@/lib/types";
import { useNow } from "@/lib/use-now";
import { cn, formatDuration, formatPhoneNumber } from "@/lib/utils";
import { useCampaignAnswers } from "@/softphone/acd-call";
import { useRepAvailability } from "@/softphone/availability";
import { DialStrip } from "@/softphone/dial-strip";
import { SeenBeforeLine } from "@/softphone/seen-before";
import { useSoftphone } from "@/softphone/softphone-context";
import { seesCampaigns } from "@/lib/types";

/**
 * The dialer on the rep's phone (ADR-019): the lead it offers in preview, and the lead it is
 * calling for them.
 *
 * In the dock, not on the Phone page, for the reason every phone control is there: an offer lasts
 * about a minute, and the rep may be on any screen when it comes. The dock opens itself for an
 * offer as it does for a ringing call.
 */

/** What the dialer is doing with this rep: the lead it offers, and whether it is calling one. */
export function useDialerWork(): { offer: DialerOffer | null; dialing: boolean } {
  const { user } = useAuth();
  const { data: offer } = useDialerOffer({ enabled: Boolean(user?.sip_endpoint_id) });
  const dialing = useRepAvailability()?.view.state === "dialing";
  return { offer: offer ?? null, dialing };
}

/**
 * A preview offer — who the lead is, how often they have been called, the time left to choose —
 * with Dial and Skip; then, once dialed, or in power mode where the dialer does not ask, the same
 * lead while the dialer calls them (`Calling`).
 *
 * A lead the dialer reaches is held for this rep only for the campaign's abandon window, two
 * seconds by default, before the call is abandoned. The ACD rings the rep's phone at that moment,
 * not at Dial, so a rep who has to click Answer misses most of them: while neither this browser's
 * auto-answer nor the campaign's is on, the view says so and offers to turn the browser's on.
 *
 * `wide` is the Phone page's stage: the dial is drawn across its width as one strip, with the
 * lead where a call's bar has its party, so nothing jumps when the lead is brought to the rep.
 * The offer keeps its column, in the middle of the stage.
 *
 * `facts` false leaves out what the dialer knows about the lead besides their name and number
 * (how often they were called, where they are, the list's columns, their record). The Phone page
 * asks for that while it has the lead's own card and form under the dial: the same person twice
 * on one screen is one of them to read past.
 */
export function DialerView({ wide = false, facts = true }: { wide?: boolean; facts?: boolean }) {
  const phone = useSoftphone();
  const answeredForYou = useCampaignAnswers();
  const { offer } = useDialerWork();
  const act = useOfferAction();
  const now = useNow(offer?.expires_at ? 1_000 : null);
  // The lead just dialed. Its offer has left the server by the time the dialer is calling them, so
  // `useOfferAction` keeps it — in the query cache, where closing the phone does not lose it.
  const dialedLead = useDialedLead();
  const [error, setError] = React.useState<string | null>(null);

  const done = act.isSuccess ? act.variables : undefined;
  const dialed =
    offer === null ||
    (done?.action === "dial" && done.offer.campaign_lead_id === offer.campaign_lead_id);
  const skipped =
    done?.action === "skip" && done.offer.campaign_lead_id === offer?.campaign_lead_id;

  const run = (action: "dial" | "skip", chosen: DialerOffer) => {
    setError(null);
    act.mutate(
      { action, offer: chosen },
      {
        onError: (e) =>
          setError(
            e instanceof ApiError
              ? e.message
              : action === "dial"
                ? "Could not dial this lead."
                : "Could not skip this lead.",
          ),
      },
    );
  };

  // In a row under the dial on the Phone page, which has the width for the sentence and its
  // button side by side; stacked in the offer's column and in the phone's own panel.
  const autoAnswerHint = (row: boolean) =>
    !phone.autoAnswer &&
    !answeredForYou && (
      <Callout tone="warning" className={cn(row && "items-center")}>
        <div className={cn(row && "flex flex-wrap items-center gap-x-4 gap-y-2")}>
          <span>
            A lead the dialer reaches is held for you for only a moment. With auto-answer on, the
            call connects without a click.
          </span>
          <Button
            variant="outline"
            size="sm"
            className={row ? undefined : "mt-2 flex"}
            onClick={() => phone.setAutoAnswer(true)}
          >
            <Zap />
            Turn on auto-answer
          </Button>
        </div>
      </Callout>
    );

  if (dialed) {
    return (
      <Calling
        chosen={offer ?? recentDialedLead(dialedLead, now)}
        hint={autoAnswerHint(wide)}
        wide={wide}
        facts={facts}
      />
    );
  }

  const left = secondsLeft(offer.expires_at, now);

  return (
    <div className={cn("flex flex-col gap-3", wide && "mx-auto max-w-md p-8")}>
      <LeadSummary
        item={offer}
        facts={facts}
        corner={
          left !== null && (
            <span
              className={cn(
                "shrink-0 rounded-md px-1.5 py-0.5 font-mono text-xs tabular",
                left <= 10 ? "bg-warning-2 text-warning-11" : "bg-surface-sunken text-text-muted",
              )}
              title="Time left to choose"
            >
              {formatDuration(left)}
            </span>
          )
        }
      />

      {skipped ? (
        <p role="status" className="text-center text-xs text-text-muted">
          Skipped. The dialer offers you the next lead when it has one.
        </p>
      ) : left === 0 ? (
        <p role="status" className="text-center text-xs text-text-muted">
          This offer has ended, and the lead goes back to the list.
        </p>
      ) : (
        <>
          {/* The lead's history, one line before Dial, which never waits for it (ACL-409). */}
          <SeenBeforeLine leadId={offer.lead.id} />
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              className="h-11 text-sm"
              disabled={act.isPending}
              loading={act.isPending && act.variables?.action === "skip"}
              onClick={() => run("skip", offer)}
            >
              <SkipForward />
              Skip
            </Button>
            <Button
              variant="default"
              className="h-11 text-sm"
              disabled={act.isPending}
              loading={act.isPending && act.variables?.action === "dial"}
              onClick={() => run("dial", offer)}
            >
              <Phone />
              Dial
            </Button>
          </div>
        </>
      )}

      {autoAnswerHint(false)}
      {error && <Callout tone="danger">{error}</Callout>}
    </div>
  );
}

/**
 * The dialer calling a lead for this rep, until the lead is brought to their phone: the lead as an
 * offer shows them, and how far the call has got.
 *
 * The lead is the server's (`GET /dialer/current`), because power mode offers nothing first. After
 * a preview Dial, the offer the rep `chosen` names them until the dialer has written the call.
 *
 * It says what the dialer does with the answer (ADR-104 in `platform`). On a campaign whose
 * detection decides for itself nobody is brought to the rep at the answer: the dialer is checking
 * for a voicemail, rings the rep only for a person, and hangs up on a machine. That hangup takes
 * the dial off `GET /dialer/current` within a moment, and in power mode the next dial is on screen
 * at once, so the view keeps asking what the switch heard on the dial it saw answered
 * (`useWatchedDial`) and says "Voicemail detected" for a few seconds after (`voicemailNote`). A
 * machine the dialer leaves the cadence's message on by itself (ADR-109) goes the same way: the
 * rep is let go at the verdict and their next dial replaces this one, so the note says the dialer
 * is leaving the message.
 *
 * A call that carries redials (ADR-105) is drawn as a run: a row of its attempts, the call and
 * each redial, with how the ones made went and which is ringing now. While a redial is on its way
 * the title is "Redialing, 1 of 2", the line under it says why it is made and the number the lead
 * sees, and Stop redialing ends the run: the dialer hangs the redial up and calls the rep's next
 * lead. An attempt the cadence leaves a message on says which message before anybody answers.
 *
 * On the Phone page (`wide`) all of that is one strip, three lines tall whatever the dial is
 * doing: who is being called, how long ago and Stop redialing; the row that is the dial itself
 * (`DialStrip`: the run's attempts, and the one being made opened up into its steps); and one
 * line of words under it. It was a bar, a strip of steps, a title with two sentences, a row of
 * tiles and a button as wide as the tiles, which said the same call in two places and left the
 * form under it a third of the page. The phone's own panel has no room for the row and keeps the
 * title, the sentences and the tiles (`RedialRun`).
 */
function Calling({
  chosen, hint, wide, facts,
}: {
  chosen: DialerOffer | null;
  hint: React.ReactNode;
  wide: boolean;
  /** Whether the lead's facts are drawn with the dial (`DialerView`). */
  facts: boolean;
}) {
  const { user } = useAuth();
  const { data: call } = useDialerCall({ enabled: Boolean(user?.sip_endpoint_id) });
  const [watched, watch] = useWatchedDial();
  const [heard, hear] = useHeardVoicemail();
  const now = useNow(call || heard ? 1_000 : null);
  const shown: LeadItem | null = call ?? chosen;
  // A rep reads no campaign (ACL-652).
  const campaign = seesCampaigns(user?.role) ? (shown?.campaign_name ?? null) : null;
  // Before the dialer has written the call there is no stage to read: the call is being placed.
  const stage = call?.stage ?? "requested";
  const seconds = call ? secondsSince(call.started_at, now) : 0;
  const detection = call?.detection ?? null;
  const answer = answerState(detection);

  // An answered dial the dialer is listening to: remember it, and ask what the switch heard
  // until it has said. The verdict usually comes after the dial has left this view.
  const callId = call?.call_id ?? null;
  const listenedTo = call !== null && call !== undefined && stage === "answered" && answer !== "bringing";
  React.useEffect(() => {
    if (!call || !listenedTo || watched?.callId === call.call_id) return;
    watch({
      callId: call.call_id,
      leadId: call.lead.id,
      name: leadName(call.lead) ?? formatPhoneNumber(call.lead.phone),
    });
    // `call` is this render's: the dial's id and what is done with its answer are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId, listenedTo, watched?.callId]);
  const verdict = useCallDetection(watched?.callId ?? null, watched !== null).data;
  const machine =
    watched !== null &&
    verdict?.call_id === watched.callId &&
    !verdict.passive &&
    verdict.result === "machine";
  // A machine on an attempt the cadence leaves a message on is not hung up on. Where a rep leaves
  // it, the dialer brings it to the rep (`comes`); where the dialer leaves it by itself, the rep
  // is let go and the message plays (`leaves`, ADR-109 in `platform`). Either read may say so,
  // the dial's own or the verdict's.
  const says = (choice: "leave_message" | "leave_message_auto") =>
    detection?.on_voicemail === choice || (machine && verdict?.on_voicemail === choice);
  const comes = says("leave_message");
  const leaves = says("leave_message_auto");
  React.useEffect(() => {
    // A voicemail that left the rep's hands is remembered, with what the dialer did about it:
    // hung up, or is leaving the message. One that comes to the rep needs no note.
    if (!watched || !machine || comes || heard?.callId === watched.callId) return;
    hear({ ...watched, at: Date.now(), leaving: leaves });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machine, comes, leaves, watched?.callId, heard?.callId]);

  // The dial on screen heard a machine: its own line says so, whichever of the two reads said it.
  const voicemail =
    stage === "answered" &&
    (answer === "voicemail" ||
      answer === "message" ||
      answer === "leaving" ||
      (machine && watched?.callId === callId));
  const heardMachine: DialDetection = {
    mode: "basic",
    passive: false,
    result: "machine",
    on_voicemail: comes ? "leave_message" : leaves ? "leave_message_auto" : "hang_up",
  };
  const redial = redialTitle(call, stage);
  const title = redial ?? dialStageText(stage, voicemail ? heardMachine : detection);
  const note = voicemailNote(heard, call ?? null, now);

  // The call's redials (ADR-105): the attempts, where this one calls from, what a voicemail gets
  // on it, and the way out of the run. All of it is about a dial nobody has answered yet, except
  // the attempts, which stay while the answer is dealt with.
  const outcomes = useCallDispositions(
    call?.campaign_id ?? null,
    Boolean(call?.campaign_id) && (call?.earlier ?? []).some((a) => a.disposition !== null),
  );
  const outcomeLabel = (slug: string) => outcomes.data?.find((d) => d.slug === slug)?.label;
  const tiles = attemptTiles(call, stage, voicemail, outcomeLabel);
  const unanswered = stage !== "answered";
  const from = unanswered ? attemptFromText(call, formatPhoneNumber) : null;
  const message = unanswered ? attemptVoicemailText(call, detection) : null;
  const wait = dialWaitText(stage, voicemail ? heardMachine : detection, message !== null);
  const redialing = redial !== null;
  const stop = useStopRedials();
  const stopped = stop.variables === callId;
  const stopError = !(stop.isError && stopped)
    ? null
    : stop.error instanceof ApiError
      ? stop.error.message
      : "Could not stop the redials.";
  const stopRun =
    canStopRedials(call, stage) && callId !== null
      ? { busy: stopped && (stop.isPending || stop.isSuccess), go: () => stop.mutate(callId) }
      : null;

  if (wide) {
    const heardNow = voicemail ? heardMachine : detection;
    const parts = dialStrip(call, stage, heardNow, voicemail, outcomeLabel);
    const said = stripVoicemailNote(heard, call ?? null, now);
    const name = shown ? leadName(shown.lead) : null;
    const number = shown ? formatPhoneNumber(shown.lead.phone) : null;
    const under = (shown !== null && facts) || Boolean(hint) || stopError !== null;
    return (
      <>
        <div className="@container/dial flex flex-col gap-2 px-4 pb-3 pt-2.5">
          {/* Who, how long and what now, where a call's bar has them (`CallBar` in
              `routes/phone.tsx`), so the party does not move when the lead is brought over. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span
              className={cn(
                "flex size-10 shrink-0 items-center justify-center rounded-full",
                voicemail ? "bg-warning-4 text-warning-11" : "bg-accent-3 text-accent-11",
              )}
            >
              {voicemail ? (
                <Voicemail className="size-[18px]" aria-hidden />
              ) : (call?.redial?.n ?? 0) >= 1 ? (
                // A redial for as long as it is one, answered or not, as its place in the row says.
                <RotateCcw className="size-[18px]" aria-hidden />
              ) : (
                <PhoneOutgoing className="size-[18px]" aria-hidden />
              )}
            </span>
            <div className="min-w-0 flex-1 basis-40">
              <p className="truncate text-base font-semibold text-text">
                {name ?? number ?? "Calling a lead"}
              </p>
              <p className="truncate text-xs text-text-muted">
                {name && number && <span className="font-mono">{number}</span>}
                {name && number && campaign && " · "}
                {campaign ?? (shown ? "A lead for you" : "The dialer is placing the call")}
              </p>
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-3">
              {seconds > 0 && (
                <span
                  className="font-mono text-sm tabular text-text"
                  title="Time since the dialer called them"
                >
                  {formatDuration(seconds)}
                </span>
              )}
              {stopRun && (
                <Button
                  variant="outline"
                  size="sm"
                  loading={stopRun.busy}
                  disabled={stopRun.busy}
                  title="Hangs up this redial and makes no more for this call. The dialer calls your next lead."
                  onClick={stopRun.go}
                >
                  {!stopRun.busy && <Square />}
                  Stop redialing
                </Button>
              )}
            </div>
          </div>
          <DialStrip parts={parts} />
          {/* The words for it, on one line: a voicemail on the dial before this one where the
              row does not say it (`stripVoicemailNote`), where this dial calls from and why, what
              the rep is waiting for, and what a voicemail gets. Until the lead answers, the row
              above has said how far the dial has got, so the sentence for that is read out and
              not drawn; from the answer on it leads the line, since what is done with an answer
              is more than a step's name. The line keeps its height when it has nothing to say,
              so the page under the dial does not move. */}
          <div
            role="status"
            aria-live="polite"
            // `relative`: the containing block of the sentence that is only read out.
            className="relative flex min-h-4 flex-wrap items-center gap-x-3 gap-y-0.5 text-xs"
          >
            {said && (
              <span className="inline-flex items-center gap-1 font-medium text-warning-11">
                <Voicemail className="size-3.5 shrink-0" aria-hidden />
                {said}
              </span>
            )}
            <span
              className={cn(
                "font-semibold",
                voicemail ? "text-warning-11" : "text-text",
                unanswered && "sr-only",
              )}
            >
              {shown ? title : "Calling a lead"}
            </span>
            {from && <span className="text-text">{from}</span>}
            {wait && <span className="text-text-muted">{wait}</span>}
            {message && (
              <span className="inline-flex items-center gap-1 text-text">
                <Voicemail className="size-3.5 shrink-0 text-accent-11" aria-hidden />
                {message}
              </span>
            )}
          </div>
        </div>
        {under && (
          <div className="flex flex-col gap-3 border-t border-border px-4 py-3">
            {stopError && <Callout tone="danger">{stopError}</Callout>}
            {shown && facts && <LeadFacts item={shown} wide />}
            {hint}
          </div>
        )}
      </>
    );
  }

  const run = (tiles || message) && (
    <RedialRun tiles={tiles} message={message} stop={stopRun} error={stopError} />
  );

  return (
    <div className="flex flex-col gap-3">
      {shown && (
        <LeadSummary
          item={shown}
          corner={
            seconds > 0 && (
              <span
                className="shrink-0 rounded-md bg-surface-sunken px-1.5 py-0.5 font-mono text-xs tabular text-text-muted"
                title="Time since the dialer called them"
              >
                {formatDuration(seconds)}
              </span>
            )
          }
        />
      )}
      <div className="flex items-center gap-3" role="status" aria-live="polite">
        {voicemail ? (
          <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-warning-4 text-warning-11">
            <Voicemail className="size-4" aria-hidden />
          </span>
        ) : (
          <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-accent-3 text-accent-11">
            {redialing ? <RotateCcw className="size-4" aria-hidden /> : <Spinner className="size-4" />}
          </span>
        )}
        <div className="min-w-0">
          <p className="text-base font-semibold text-text">{shown ? title : "Calling a lead"}</p>
          {from && <p className="text-xs text-text">{from}</p>}
          {wait && <p className="text-xs text-text-muted">{wait}</p>}
        </div>
      </div>
      {run}
      {note && (
        <p
          role="status"
          className="flex items-start gap-1.5 rounded-md border border-warning-6 bg-warning-2 px-2.5 py-1.5 text-xs font-medium text-warning-11"
        >
          <Voicemail className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          {note}
        </p>
      )}
      {hint}
    </div>
  );
}

/**
 * A call's run of redials in the phone's own panel (ADR-105 in `platform`): its attempts as a row
 * of tiles, what a voicemail gets on the one being made, and Stop redialing. The Phone page has
 * the width to draw the run and the dial's steps as one row instead (`DialStrip`).
 *
 * The tiles read left to right: the call, then each redial. One that is over is dimmed and says
 * how it went, the one being made is in the accent tone with how far it has got, and the ones
 * to come say when they are made, which is only if nobody answers. Stop redialing is there from
 * the first redial on: the call itself is a call like any other, and the rep cannot skip it.
 * A call with no redials has no tiles, and still says the message a voicemail gets on it.
 */
function RedialRun({
  tiles, message, stop, error,
}: {
  tiles: AttemptTile[] | null;
  message: string | null;
  stop: { busy: boolean; go: () => void } | null;
  error: string | null;
}) {
  return (
    <div className="flex flex-col gap-2">
      {tiles && (
        <ol
          className="grid gap-1.5"
          style={{ gridTemplateColumns: `repeat(${tiles.length}, minmax(0, 1fr))` }}
          aria-label="This call and its redials"
        >
          {tiles.map((tile) => (
            <li
              key={tile.label}
              aria-current={tile.state === "current" ? "step" : undefined}
              className={cn(
                "min-w-0 rounded-lg border px-2 py-1.5",
                tile.state === "done" && "border-transparent bg-surface-sunken",
                tile.state === "current" && "border-accent-8 bg-accent-2",
                tile.state === "todo" && "border-border",
              )}
            >
              <p
                className={cn(
                  "flex items-center gap-1 text-xs font-semibold",
                  tile.state === "done" ? "text-text-muted" : "text-text",
                )}
              >
                {tile.state === "done" ? (
                  <CircleX className="size-3 shrink-0" aria-hidden />
                ) : tile.state === "current" ? (
                  <Spinner className="size-3 shrink-0 text-accent-11" />
                ) : (
                  <Circle className="size-3 shrink-0 text-text-subtle" aria-hidden />
                )}
                <span className="truncate">{tile.label}</span>
              </p>
              <p
                className={cn(
                  "mt-0.5 text-2xs leading-tight",
                  tile.state === "current" ? "font-medium text-accent-11" : "text-text-muted",
                )}
              >
                {tile.text}
              </p>
            </li>
          ))}
        </ol>
      )}
      {message && (
        <p className="flex items-start gap-1.5 rounded-md border border-accent-4 bg-accent-2 px-2.5 py-1.5 text-xs text-text">
          <Voicemail className="mt-0.5 size-3.5 shrink-0 text-accent-11" aria-hidden />
          {message}
        </p>
      )}
      {stop && (
        <Button
          variant="outline"
          className="h-9 text-sm"
          loading={stop.busy}
          disabled={stop.busy}
          title="Hangs up this redial and makes no more for this call. The dialer calls your next lead."
          onClick={stop.go}
        >
          <Square />
          Stop redialing
        </Button>
      )}
      {error && <Callout tone="danger">{error}</Callout>}
    </div>
  );
}

/** What an offer and a call being placed both say about their lead. */
type LeadItem = Pick<
  DialerCall,
  "campaign_id" | "campaign_name" | "attempts" | "last_disposition" | "lead"
>;

/**
 * The lead as the dialer shows them, offered or being called: the campaign, the name and number,
 * how often they have been called and how the last call went, where they are, their record, and a
 * few of the list's own columns. `corner` sits beside the name: the time left to choose, or how
 * long the call has been going.
 */
function LeadSummary({
  item, corner, facts = true,
}: {
  item: LeadItem;
  corner: React.ReactNode;
  facts?: boolean;
}) {
  const { lead } = item;
  const name = leadName(lead);
  const sees = seesCampaigns(useAuth().user?.role);

  return (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-text-muted">
            {(sees ? item.campaign_name : null) ?? "A lead for you"}
          </p>
          <p className="truncate text-lg font-semibold text-text">
            {name ?? formatPhoneNumber(lead.phone)}
          </p>
          {name && (
            <p className="truncate font-mono text-xs text-text-muted">
              {formatPhoneNumber(lead.phone)}
            </p>
          )}
        </div>
        {corner}
      </div>
      {facts && <LeadFacts item={item} />}
    </>
  );
}

/**
 * What the dialer knows about the lead besides their name: how often they have been called and
 * how the last call went, where they are, their record, and a few of the list's own columns.
 * `wide` lays the columns out across the Phone page's stage.
 */
function LeadFacts({ item, wide = false }: { item: LeadItem; wide?: boolean }) {
  const { lead } = item;
  const outcomes = useCallDispositions(
    item.campaign_id,
    item.campaign_id !== null && item.last_disposition !== null,
  );
  const place = leadPlace(lead);
  const details = leadDetails(lead.attributes);
  const label = (slug: string) => outcomes.data?.find((d) => d.slug === slug)?.label;

  return (
    <>
      <p className="flex flex-wrap items-center gap-x-2 text-xs text-text-muted">
        <span>
          {attemptsText(item, label)}
          {place && ` · ${place}`}
        </span>
        {/* The record — the timeline, the notes — before the rep is talking to them (ADR-044). */}
        <Link to={`/contacts/${lead.id}`} className="inline-flex items-center gap-1 font-medium text-accent-11 hover:underline">
          <ExternalLink className="size-3" aria-hidden />
          Open record
        </Link>
      </p>

      {details.length > 0 &&
        (wide ? (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border border-border px-4 py-3 md:grid-cols-4">
            {details.map(([key, value]) => (
              <div key={key} className="min-w-0">
                <dt className="truncate text-xs capitalize text-text-muted">{key}</dt>
                <dd className="truncate text-sm text-text">{value}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg border border-border px-3 py-2 text-xs">
            {details.map(([key, value]) => (
              <React.Fragment key={key}>
                <dt className="capitalize text-text-muted">{key}</dt>
                <dd className="min-w-0 truncate text-text">{value}</dd>
              </React.Fragment>
            ))}
          </dl>
        ))}
    </>
  );
}
